<template>
  <div class="onboarding-container" :class="`step-${currentStep}`">
    <!-- Progress Bar -->
    <div class="progress-bar">
      <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
    </div>

    <!-- Step Indicator -->
    <div class="step-indicator">
      <div
        v-for="step in totalSteps"
        :key="step"
        class="step-dot"
        :class="{
          active: step === currentStep,
          completed: step < currentStep,
        }"
      ></div>
    </div>

    <!-- Content Container -->
    <transition name="slide-fade" mode="out-in">
      <div :key="currentStep" class="step-content">
        <!-- Step 1: Bienvenue -->
        <div v-if="currentStep === 1" class="step-card welcome-step">
          <div class="icon-container">
            <img src="@/assets/flamou/happy.png" alt="Flamou Happy" class="flamou-icon" />
          </div>

          <h1 class="step-title">Bienvenue sur Autrement Cap'</h1>
          <p class="step-description">
            Vous allez aider un jeune à révéler ses talents et à construire son avenir.
            Merci d'être là pour lui 🧡
          </p>

          <p class="step-description">
            Ensemble, valorisons ses compétences au quotidien, expérimentons-les dans le monde professionnel, 
            et posons les premiers jalons de son projet de vie.
          </p>

          <div class="feature-list">
            <div class="feature-item">
              <span class="feature-icon">✓</span>
              <span><strong>Un rôle clé :</strong> observer et noter les réussites du jeune dans la vraie vie</span>
            </div>
            <div class="feature-item">
              <span class="feature-icon">✓</span>
              <span><strong>Une interface simple :</strong> un accompagnement pas à pas pour bien démarrer</span>
            </div>
            <div class="feature-item">
              <span class="feature-icon">✓</span>
              <span><strong>Un impact concret :</strong> vos observations construisent son portfolio de compétences</span>
            </div>
            <div class="feature-item">
              <span class="feature-icon">✓</span>
              <span><strong>Une aventure collective :</strong> parents, éducateurs et proches participent ensemble</span>
            </div>
          </div>

          <div class="step-navigation">
            <button class="btn btn-primary" @click="nextStep">
              Est ce que je suis prêt(e) ?
            </button>
          </div>
        </div>

        <!-- Step 2: Rôle et expérience -->
        <div v-if="currentStep === 2" class="step-card role-step">
          <div class="icon-container">
            <div class="animated-icon">💼</div>
          </div>
          <h1 class="step-title">Qui êtes vous ?</h1>
          <p class="step-description">
            Qui êtes-vous pour le(s) jeune(s) que vous accompagnez ?
          </p>

          <div class="role-selector">
            <div
              v-for="role in roles"
              :key="role.id"
              class="role-card"
              :class="{ selected: formData.role === role.id }"
              @click="selectRole(role.id)"
            >
              <div class="role-icon">{{ role.icon }}</div>
              <h3>{{ role.name }}</h3>
              <p>{{ role.description }}</p>
            </div>
          </div>

          <!-- Navigation Buttons -->
          <div class="step-navigation">
            <button
              class="btn btn-secondary"
              @click="previousStep"
            >
              <span class="btn-icon">←</span>
              Précédent
            </button>
            <button
              class="btn btn-primary"
              :disabled="!canProceed"
              @click="nextStep"
            >
              Suivant
              <span class="btn-icon">→</span>
            </button>
          </div>
        </div>

        <!-- Step 3: Informations personnelles -->
        <div v-if="currentStep === 3" class="step-card info-step">
          <div class="icon-container">
            <div class="animated-icon">👤</div>
          </div>
          <h1 class="step-title">Vos informations</h1>
          <p class="step-description">
            Parlez-nous un peu de vous pour personnaliser votre profil
          </p>

          <form class="onboarding-form" @submit.prevent>
            <div class="form-group">
              <label for="firstName">Prénom *</label>
              <input
                id="firstName"
                v-model="formData.firstName"
                type="text"
                placeholder="Jean"
                required
                @input="capitalizeFirstName"
              />
            </div>

            <div class="form-group">
              <label for="lastName">Nom *</label>
              <input
                id="lastName"
                v-model="formData.lastName"
                type="text"
                placeholder="Dupont"
                required
                @input="capitalizeLastName"
              />
            </div>

            <div class="form-group">
              <label for="email">Email *</label>
              <input
                id="email"
                v-model="formData.email"
                type="email"
                placeholder="jean.dupont@exemple.fr"
                required
                @input="emailError = ''"
              />
              <span v-if="emailError" class="email-error">
                ⚠ {{ emailError }}
              </span>
            </div>

            <div class="form-group">
              <label for="phone">Téléphone</label>
              <input
                id="phone"
                v-model="formData.phone"
                type="tel"
                placeholder="+33 6 12 34 56 78"
              />
            </div>
          </form>

          <!-- Navigation Buttons -->
          <div class="step-navigation">
            <button
              class="btn btn-secondary"
              @click="previousStep"
            >
              <span class="btn-icon">←</span>
              Précédent
            </button>
            <button
              class="btn btn-primary"
              :disabled="!canProceed || checkingEmail"
              @click="nextStep"
            >
              <span v-if="checkingEmail">Vérification...</span>
              <span v-else>Suivant</span>
              <span class="btn-icon">→</span>
            </button>
          </div>
        </div>

        <!-- Step 4: Création du mot de passe -->
        <div v-if="currentStep === 4" class="step-card password-step">
          <div class="icon-container">
            <div class="animated-icon">🔐</div>
          </div>
          <h1 class="step-title">Sécurisez votre compte</h1>
          <p class="step-description">
            Créez un mot de passe fort pour protéger votre compte
          </p>

          <form class="onboarding-form" @submit.prevent>
            <div class="form-group">
              <label for="password">Mot de passe *</label>
              <div class="password-input-wrapper">
                <input
                  id="password"
                  v-model="formData.password"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="Minimum 8 caractères"
                  required
                  autocomplete="new-password"
                />
                <button
                  type="button"
                  class="toggle-password-btn"
                  @click="showPassword = !showPassword"
                  tabindex="-1"
                >
                  {{ showPassword ? '👁️' : '👁️‍🗨️' }}
                </button>
              </div>
              <div class="password-strength">
                <div
                  class="strength-bar"
                  :class="passwordStrengthClass"
                  :style="{ width: passwordStrength + '%' }"
                ></div>
              </div>
              <span class="password-hint">{{ passwordStrengthText }}</span>
            </div>

            <div class="form-group">
              <label for="confirmPassword">Confirmer le mot de passe *</label>
              <div class="password-input-wrapper">
                <input
                  id="confirmPassword"
                  v-model="formData.confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  placeholder="Retapez votre mot de passe"
                  required
                  autocomplete="new-password"
                />
                <button
                  type="button"
                  class="toggle-password-btn"
                  @click="showConfirmPassword = !showConfirmPassword"
                  tabindex="-1"
                >
                  {{ showConfirmPassword ? '👁️' : '👁️‍🗨️' }}
                </button>
              </div>
              <span v-if="passwordMismatch" class="password-error">
                ⚠ Les mots de passe ne correspondent pas
              </span>
            </div>

            <div class="password-requirements">
              <div class="requirements-grid">
                <div class="requirement" :class="{ met: passwordRequirements.length }">
                  <span class="requirement-icon">{{ passwordRequirements.length ? '✓' : '○' }}</span>
                  <span>8+ caractères</span>
                </div>
                <div class="requirement" :class="{ met: passwordRequirements.uppercase }">
                  <span class="requirement-icon">{{ passwordRequirements.uppercase ? '✓' : '○' }}</span>
                  <span>Majuscule</span>
                </div>
                <div class="requirement" :class="{ met: passwordRequirements.lowercase }">
                  <span class="requirement-icon">{{ passwordRequirements.lowercase ? '✓' : '○' }}</span>
                  <span>Minuscule</span>
                </div>
                <div class="requirement" :class="{ met: passwordRequirements.number }">
                  <span class="requirement-icon">{{ passwordRequirements.number ? '✓' : '○' }}</span>
                  <span>Chiffre</span>
                </div>
              </div>
            </div>
          </form>

          <!-- Navigation Buttons -->
          <div class="step-navigation">
            <button
              class="btn btn-secondary"
              @click="previousStep"
            >
              <span class="btn-icon">←</span>
              Précédent
            </button>
            <button
              class="btn btn-primary"
              :disabled="!canProceed || registering"
              @click="registerUser"
            >
              <span v-if="registering">Création du compte...</span>
              <span v-else>Créer mon compte</span>
              <span class="btn-icon">→</span>
            </button>
          </div>
        </div>

        <!-- Step 5: Vérification Email -->
        <div v-if="currentStep === 5" class="step-card verification-step">
          <!-- Email vérifié avec succès -->
          <div v-if="formData.emailVerified" class="verified-success">
            <div class="icon-container">
              <div class="animated-icon success-pulse">🎉</div>
            </div>
            <h1 class="step-title">Email vérifié !</h1>
            <p class="step-description">
              Félicitations ! Votre compte a été activé avec succès.
            </p>

            <div class="success-details-box">
              <div class="success-item">
                <span class="success-check">✓</span>
                <span>Compte vérifié</span>
              </div>
              <div class="success-item">
                <span class="success-check">✓</span>
                <span>Connexion établie</span>
              </div>
              <div class="success-item">
                <span class="success-check">✓</span>
                <span>Prêt à commencer</span>
              </div>
            </div>

            <div class="welcome-message">
              <p>🚀 Vous êtes maintenant prêt à accompagner vos jeunes !</p>
            </div>

            <!-- Bouton pour accéder au dashboard -->
            <div class="step-navigation">
              <button
                class="btn btn-success btn-large"
                @click="goToDashboard"
              >
                Accéder à votre espace personnel
                <span class="btn-icon">→</span>
              </button>
            </div>
          </div>

          <!-- En attente de vérification -->
          <div v-else>
            <div class="icon-container">
              <div class="animated-icon">📧</div>
            </div>
            <h1 class="step-title">Vérifiez votre email</h1>
            <p class="step-description">
              Un lien de vérification a été envoyé à
              <strong>{{ formData.email }}</strong>
            </p>

            <div class="verification-container-compact">
              <!-- Indicateur de vérification en cours -->
              <div class="checking-status-compact">
                <div class="checking-spinner"></div>
                <p>En attente de la vérification...</p>
              </div>

              <!-- Message de succès (email vérifié) -->
              <div v-if="verificationError && verificationError.includes('vérifié')" class="success-message">
                <span class="success-icon">✓</span>
                <span>{{ verificationError }}</span>
              </div>

              <!-- Message d'erreur -->
              <div v-else-if="verificationError" class="error-message">
                <span class="error-icon">✗</span>
                <span>{{ verificationError }}</span>
              </div>

              <!-- Bouton renvoyer -->
              <div class="resend-container-compact">
                <button
                  v-if="resendCooldown === 0"
                  @click="resendVerificationEmail"
                  class="resend-button-compact"
                  :disabled="resending"
                >
                  <span v-if="resending">Envoi...</span>
                  <span v-else>Renvoyer l'email</span>
                </button>
                <span v-else class="resend-cooldown">
                  Renvoyer dans {{ resendCooldown }}s
                </span>
              </div>

              <!-- Note importante -->
              <p class="verification-note-compact">
                💡 Vérifiez vos spams • ⏱ Lien valide 24h
              </p>
            </div>
          </div>
        </div>

      </div>
    </transition>

  </div>
</template>

<script>
import AuthService from '@/services/AuthService'

export default {
  name: 'AccompagnateurOnboarding',
  async mounted() {
    // Vérifier si un utilisateur est déjà connecté
    try {
      const authStatus = await AuthService.isAuthenticated()

      if (authStatus.authenticated) {
        // Les role_id des accompagnateurs : Enseignant(10), Psychologue(11), Famille(12)
        const accompagnateurRoleIds = [10, 11, 12]
        const userRoleId = parseInt(authStatus.role)

        if (accompagnateurRoleIds.includes(userRoleId)) {
          // Si c'est un accompagnateur, rediriger vers le dashboard
          this.$router.push('/accompagnant/dashboard')
          return
        }

        // Si c'est un autre type d'utilisateur (ex: Young Person = 8), le déconnecter
        console.log('Déconnexion du compte existant pour créer un compte accompagnateur...')
        await AuthService.logout()
      }
    } catch (error) {
      // Pas de compte connecté, continuer normalement
      console.log('Aucun compte connecté, continuer l\'onboarding')
    }

    // Écouter les changements de localStorage (pour détecter la connexion depuis un autre onglet)
    window.addEventListener('storage', this.handleStorageChange)
  },
  data() {
    return {
      currentStep: 1,
      totalSteps: 5,

      // Vérification email
      emailSent: false,
      verificationError: '',
      resendCooldown: 0,
      resendTimer: null,
      resending: false,
      checkingVerification: false,
      verificationCheckInterval: null,

      // Registration state
      registering: false,
      userId: null,
      registrationError: '',

      // Password visibility
      showPassword: false,
      showConfirmPassword: false,

      // Email validation
      checkingEmail: false,
      emailError: '',

      formData: {
        firstName: '',
        lastName: '',
        email: '',
        address: '',
        phone: '',
        password: '',
        confirmPassword: '',
        role: '',
        emailVerified: false,
      },
      roles: [
        {
          id: 'Psychologue',
          name: 'Thérapeute',
          icon: '🧠',
          description: 'Psychologue, psychomotricien, ergothérapeute, Structure accompagnement… ',
        },
        {
          id: 'Enseignant',
          name: 'Educateur',
          icon: '📚',
          description: 'Professeur, enseignant, éducateur...',
        },
        {
          id: 'Famille',
          name: 'Entourage du jeune',
          icon: '🏠',
          description: 'Membre de la famille ou proche',
        },
      ],
    }
  },
  computed: {
    progressPercentage() {
      return (this.currentStep / this.totalSteps) * 100
    },
    passwordRequirements() {
      const password = this.formData.password
      return {
        length: password.length >= 8,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        number: /[0-9]/.test(password),
      }
    },
    passwordStrength() {
      const requirements = this.passwordRequirements
      const metCount = Object.values(requirements).filter(Boolean).length
      return (metCount / 4) * 100
    },
    passwordStrengthClass() {
      const strength = this.passwordStrength
      if (strength < 50) return 'weak'
      if (strength < 75) return 'medium'
      return 'strong'
    },
    passwordStrengthText() {
      const strength = this.passwordStrength
      if (strength === 0) return ''
      if (strength < 50) return 'Faible'
      if (strength < 75) return 'Moyen'
      if (strength < 100) return 'Bon'
      return 'Excellent'
    },
    passwordMismatch() {
      return (
        this.formData.confirmPassword !== '' &&
        this.formData.password !== this.formData.confirmPassword
      )
    },
    isPasswordValid() {
      return (
        Object.values(this.passwordRequirements).every(Boolean) &&
        this.formData.password === this.formData.confirmPassword &&
        this.formData.confirmPassword !== ''
      )
    },
    canProceed() {
      switch (this.currentStep) {
        case 1:
          return true
        case 2:
          return this.formData.role !== ''
        case 3:
          return (
            this.formData.firstName.trim() !== '' &&
            this.formData.lastName.trim() !== '' &&
            this.formData.email.trim() !== '' &&
            this.isValidEmail(this.formData.email)
          )
        case 4:
          return this.isPasswordValid
        case 5:
          return this.formData.emailVerified
        default:
          return false
      }
    },
  },
  watch: {
    currentStep(newStep, oldStep) {
      // Lorsqu'on arrive à l'étape de vérification (step 5)
      if (newStep === 5 && !this.emailSent) {
        // L'email est déjà envoyé par registerUser, on affiche juste le message
        this.emailSent = true
        // Démarrer la vérification périodique
        this.startVerificationCheck()
      }

      // Lorsqu'on quitte l'étape de vérification, arrêter le polling
      if (oldStep === 5 && newStep !== 5) {
        this.stopVerificationCheck()
      }
    },
  },
  beforeUnmount() {
    // Nettoyer les timers
    if (this.resendTimer) clearInterval(this.resendTimer)
    if (this.verificationCheckInterval) clearInterval(this.verificationCheckInterval)

    // Nettoyer l'écouteur d'événements storage
    window.removeEventListener('storage', this.handleStorageChange)
  },
  methods: {
    capitalizeFirstName(event) {
      const value = event.target.value
      if (value.length > 0) {
        this.formData.firstName = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
      }
    },

    capitalizeLastName(event) {
      const value = event.target.value
      if (value.length > 0) {
        this.formData.lastName = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
      }
    },

    async registerUser() {
      if (!this.isPasswordValid) {
        return
      }

      this.registering = true
      this.registrationError = ''

      try {
        // Préparer les données pour l'enregistrement
        const userData = {
          username: this.formData.email,
          email: this.formData.email,
          password: this.formData.password,
          first_name: this.formData.firstName,
          last_name: this.formData.lastName,
          phone_number: this.formData.phone || null,
          address: this.formData.address || null,
          role: this.formData.role,
        }

        // Appeler l'API de registration
        const response = await AuthService.register(userData)

        console.log('Backend response:', response)

        // Passer à l'étape de vérification email
        this.currentStep++
      } catch (error) {
        console.error('Erreur lors de la création du compte:', error)

        // Gérer les erreurs
        if (error.response?.data?.detail) {
          if (typeof error.response.data.detail === 'string') {
            this.registrationError = error.response.data.detail
          } else if (Array.isArray(error.response.data.detail)) {
            this.registrationError = error.response.data.detail
              .map((err) => err.msg || err.message)
              .join(', ')
          }
        } else {
          this.registrationError =
            'Une erreur est survenue lors de la création du compte. Veuillez réessayer.'
        }
      } finally {
        this.registering = false
      }
    },

    async nextStep() {
      if (!this.canProceed || this.currentStep >= this.totalSteps) {
        return
      }

      // Si on est à l'étape 3 (informations), vérifier l'email
      if (this.currentStep === 3) {
        await this.checkEmailAvailability()
        // Si l'email existe déjà, ne pas passer à l'étape suivante
        if (this.emailError) {
          return
        }
      }

      this.currentStep++
    },

    async checkEmailAvailability() {
      this.checkingEmail = true
      this.emailError = ''

      try {
        const isAvailable = await AuthService.checkUsernameAvailability(this.formData.email)

        if (!isAvailable) {
          this.emailError = 'Cet email est déjà utilisé. Veuillez en choisir un autre.'
        }
      } catch (error) {
        console.error('Erreur lors de la vérification de l\'email:', error)
        // En cas d'erreur réseau, on laisse passer (ne pas bloquer l'utilisateur)
        this.emailError = ''
      } finally {
        this.checkingEmail = false
      }
    },
    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--
      }
    },
    selectRole(roleId) {
      this.formData.role = roleId
    },
    getRoleName(roleId) {
      const role = this.roles.find((r) => r.id === roleId)
      return role ? role.name : ''
    },
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },

    // Renvoyer l'email de vérification
    async resendVerificationEmail() {
      if (this.resendCooldown > 0 || this.resending) return

      this.resending = true
      this.verificationError = ''

      try {
        // Appeler l'API pour renvoyer l'email
        await AuthService.request('post', '/recovery/resend-verification-email', {
          email: this.formData.email
        })

        this.emailSent = true

        // Cooldown de 60 secondes
        this.resendCooldown = 60
        this.resendTimer = setInterval(() => {
          this.resendCooldown--
          if (this.resendCooldown === 0) {
            clearInterval(this.resendTimer)
          }
        }, 1000)
      } catch (error) {
        console.error('Erreur lors du renvoi de l\'email:', error)
        this.verificationError = 'Impossible de renvoyer l\'email. Veuillez réessayer.'
      } finally {
        this.resending = false
      }
    },

    // Démarrer la vérification périodique du statut
    startVerificationCheck() {
      // Vérifier toutes les 3 secondes
      this.verificationCheckInterval = setInterval(async () => {
        await this.checkIfEmailVerified()
      }, 3000)
    },

    // Arrêter la vérification périodique
    stopVerificationCheck() {
      if (this.verificationCheckInterval) {
        clearInterval(this.verificationCheckInterval)
        this.verificationCheckInterval = null
      }
    },

    // Vérifier si l'email a été vérifié
    async checkIfEmailVerified() {
      if (this.checkingVerification) return

      this.checkingVerification = true

      try {
        // Nouvelle méthode : Vérifier le statut de vérification via l'endpoint
        const response = await AuthService.request(
          'get',
          `/recovery/check-verification-status?email=${encodeURIComponent(this.formData.email)}`
        )

        if (response.data && response.data.is_verified) {
          // L'email a été vérifié, maintenant on vérifie l'authentification locale
          try {
            const authStatus = await AuthService.isAuthenticated()

            // Si l'utilisateur est déjà connecté sur cet appareil, on passe à l'étape suivante
            if (authStatus.authenticated && authStatus.role) {
              const accompagnateurRoleIds = [10, 11, 12]
              const userRoleId = parseInt(authStatus.role)

              if (accompagnateurRoleIds.includes(userRoleId)) {
                this.formData.emailVerified = true
                this.stopVerificationCheck()
              }
            } else {
              // Email vérifié mais pas connecté sur cet appareil
              // Afficher un message pour demander à l'utilisateur de se connecter
              this.verificationError = 'Email vérifié ! Veuillez vous connecter sur cet appareil.'
              this.stopVerificationCheck()

              // Rediriger vers la page de connexion après un court délai
              setTimeout(() => {
                this.$router.push('/accompagnant/login')
              }, 2000)
            }
          } catch (authError) {
            // Erreur d'authentification locale, mais email vérifié
            this.verificationError = 'Email vérifié ! Veuillez vous connecter.'
            this.stopVerificationCheck()

            setTimeout(() => {
              this.$router.push('/accompagnant/login')
            }, 2000)
          }
        }
      } catch (error) {
        // Ignorer les erreurs de vérification en silence
        // L'utilisateur n'est probablement pas encore vérifié
        console.log('Vérification en attente...')
      } finally {
        this.checkingVerification = false
      }
    },

    // Aller au dashboard
    goToDashboard() {
      this.$router.push('/accompagnant/dashboard')
    },

    // Gérer les changements de localStorage depuis un autre onglet
    handleStorageChange(event) {
      // Vérifier si c'est le token d'accès qui a été ajouté/modifié
      if (event.key === 'accessToken' && event.newValue) {
        console.log('Token détecté depuis un autre onglet - mise à jour des headers...')

        // Mettre à jour le header Authorization dans authAxios
        AuthService.setAccessToken(event.newValue)

        // Forcer la vérification de l'authentification
        this.checkIfEmailVerified()
      }
    },
  },
}
</script>

<style scoped>
/* Container principal */
.onboarding-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px;
  position: relative;
  overflow: hidden;
}

/* Barre de progression */
.progress-bar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background-color: rgba(255, 255, 255, 0.2);
  z-index: 1000;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #FF9800, #FF9800);
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 0 10px rgba(255, 152, 0, 0.5);
}

/* Indicateur d'étapes */
.step-indicator {
  display: flex;
  gap: 10px;
  margin: 15px 0;
}

.step-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #e0e0e0;
  transition: all 0.3s ease;
  position: relative;
}

.step-dot.active {
  background-color: #FF9800;
  transform: scale(1.3);
  box-shadow: 0 0 10px rgba(255, 152, 0, 0.6);
}

.step-dot.completed {
  background-color: #FF9800;
  transform: scale(1.1);
}

.step-dot.completed::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 8px;
  font-weight: bold;
}

/* Contenu des étapes */
.step-content {
  width: 100%;
  max-width: 700px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow-y: auto;
  max-height: calc(100vh - 100px);
  padding: 0 10px;
}

.step-content::-webkit-scrollbar {
  width: 6px;
}

.step-content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
}

.step-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 10px;
}

.step-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  animation: slideIn 0.5s ease;
  margin: 10px 0;
  max-height: 100%;
  overflow-y: auto;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Icon Container */
.icon-container {
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
}

.animated-icon {
  font-size: 40px;
  animation: float 3s ease-in-out infinite;
}

.flamou-icon {
  width: 80px;
  height: auto;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

/* Titres et descriptions */
.step-title {
  font-size: 22px;
  font-weight: 700;
  color: #2c3e50;
  text-align: center;
  margin-bottom: 10px;
  line-height: 1.2;
}

.step-description {
  font-size: 14px;
  color: #546e7a;
  text-align: center;
  margin-bottom: 20px;
  line-height: 1.4;
}

/* Feature List */
.feature-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%);
  border-radius: 8px;
  font-size: 13px;
  color: #2c3e50;
  transition: transform 0.3s ease;
}

.feature-item:hover {
  transform: translateX(5px);
}

.feature-icon {
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #4caf50 0%, #8bc34a 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  flex-shrink: 0;
  font-size: 12px;
}

/* Form Styles */
.onboarding-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.form-group label {
  font-size: 12px;
  font-weight: 600;
  color: #2c3e50;
  text-align: left;
  margin-left: 4px;
}

.form-group input,
.form-group select {
  padding: 10px 12px;
  border: 2px solid #e0e7ee;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background-color: #f0f2f5;
  color: #2c3e50;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #2196f3;
  background-color: white;
  box-shadow: 0 0 0 4px rgba(33, 150, 243, 0.1);
}

/* Password Input Wrapper */
.password-input-wrapper {
  position: relative;
  width: 100%;
}

.password-input-wrapper input {
  width: 100%;
  padding-right: 45px;
}

.toggle-password-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  font-size: 20px;
  padding: 6px 8px;
  opacity: 0.6;
  transition: opacity 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.toggle-password-btn:hover {
  opacity: 1;
  background-color: rgba(0, 0, 0, 0.05);
}

.toggle-password-btn:active {
  transform: translateY(-50%) scale(0.95);
}

/* Verification Step Styles */
.verification-container-compact {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  max-width: 450px;
  margin: 0 auto;
}

.success-message {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #fff5f0 0%, #ffe8e0 100%);
  border-radius: 10px;
  color: #FF9800;
  font-weight: 600;
  width: 100%;
  justify-content: center;
}

.success-icon {
  width: 24px;
  height: 24px;
  background: #FF9800;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
}

.code-input-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  align-items: center;
}

.code-input-container label {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
}

.code-input {
  width: 100%;
  max-width: 250px;
  padding: 16px;
  font-size: 32px;
  text-align: center;
  letter-spacing: 10px;
  border: 3px solid #e0e7ee;
  border-radius: 12px;
  font-weight: 700;
  transition: all 0.3s ease;
}

.code-input:focus {
  outline: none;
  border-color: #2196f3;
  box-shadow: 0 0 0 4px rgba(33, 150, 243, 0.1);
}

.error-message {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
  border-radius: 10px;
  color: #f44336;
  font-weight: 600;
  width: 100%;
  justify-content: center;
}

.error-icon {
  width: 24px;
  height: 24px;
  background: #f44336;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
}

.expiration-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(33, 150, 243, 0.1);
  border-radius: 8px;
  color: #2196f3;
  font-weight: 600;
  font-size: 14px;
}

.clock-icon {
  font-size: 18px;
}

.resend-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}

.resend-button {
  padding: 10px 20px;
  background: linear-gradient(135deg, #64b5f6 0%, #2196f3 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
}

.resend-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
}

.resend-cooldown {
  font-size: 14px;
  color: #546e7a;
  font-weight: 600;
}

.simulation-info {
  margin-top: 20px;
  padding: 16px;
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  border-radius: 12px;
  border-left: 4px solid #FF9800;
  text-align: center;
}

.simulation-info p {
  margin: 4px 0;
  font-size: 14px;
  color: #2c3e50;
}

.simulation-info strong {
  color: #FF9800;
  font-size: 18px;
  font-family: monospace;
  letter-spacing: 2px;
}

/* Role Selector */
.role-selector {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.role-card {
  padding: 16px 12px;
  border: 2px solid #e0e7ee;
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
}

.role-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #FF9800, #FF9800);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.role-card:hover {
  border-color: #2196f3;
  transform: translateY(-5px) scale(1.02);
  box-shadow: 0 8px 24px rgba(33, 150, 243, 0.25);
  background: linear-gradient(135deg, #ffffff 0%, #e3f2fd 100%);
}

.role-card:hover::before {
  opacity: 1;
}

.role-card.selected {
  border-color: #2196f3;
  border-width: 4px;
  background: linear-gradient(135deg, #fff5f0 0%, #ffe8e0 100%);
  transform: scale(1.05);
}

.role-card.selected::before {
  opacity: 1;
  height: 6px;
  background: linear-gradient(90deg, #FF9800, #FF9800, #FF9800);
}

.role-card.selected::after {
  content: '✓';
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  background: #FF9800;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.5);
  animation: checkmarkPop 0.3s ease;
}

@keyframes checkmarkPop {
  0% {
    transform: scale(0) rotate(-180deg);
    opacity: 0;
  }
  50% {
    transform: scale(1.2) rotate(10deg);
  }
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
}

.role-icon {
  font-size: 40px;
  margin-bottom: 8px;
  filter: grayscale(20%);
  transition: all 0.3s ease;
}

.role-card:hover .role-icon {
  filter: grayscale(0%);
  transform: scale(1.1);
}

.role-card.selected .role-icon {
  filter: grayscale(0%) brightness(1.1);
  transform: scale(1.15);
}

.role-card h3 {
  font-size: 14px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 6px;
  transition: color 0.3s ease;
}

.role-card.selected h3 {
  color: #2196f3;
  font-size: 14px;
}

.role-card p {
  font-size: 12px;
  color: #546e7a;
  line-height: 1.3;
  transition: color 0.3s ease;
}

.role-card.selected p {
  color: #1565c0;
  font-weight: 500;
}

/* Verified Success State */
.verified-success {
  text-align: center;
  animation: slideIn 0.6s ease;
}

.success-pulse {
  animation: float 3s ease-in-out infinite, pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-10px) scale(1.1);
  }
}

.success-details-box {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 24px 0;
  padding: 20px;
  background: linear-gradient(135deg, #fff5f0 0%, #ffe8e0 100%);
  border-radius: 14px;
  border-left: 4px solid #FF9800;
}

.success-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
  color: #2c3e50;
  font-weight: 600;
}

.success-check {
  width: 28px;
  height: 28px;
  background: #FF9800;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  font-weight: bold;
  flex-shrink: 0;
}

.welcome-message {
  margin: 24px 0;
  padding: 16px;
  background: linear-gradient(135deg, #fff5f0 0%, #ffe8e0 100%);
  border-radius: 12px;
  border-left: 4px solid #FF9800;
}

.welcome-message p {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.btn-large {
  padding: 16px 32px;
  font-size: 17px;
}

/* Checking Status Compact */
.checking-status-compact {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 16px;
  background: linear-gradient(135deg, #fff5f0 0%, #ffe8e0 100%);
  border-radius: 12px;
  border-left: 3px solid #FF9800;
}

.checking-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e3f2fd;
  border-top: 3px solid #2196f3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.checking-status-compact p {
  font-size: 13px;
  font-weight: 600;
  color: #1976d2;
  margin: 0;
}

/* Verification Note Compact */
.verification-note-compact {
  font-size: 12px;
  color: #546e7a;
  text-align: center;
  margin: 8px 0 0 0;
  line-height: 1.5;
  padding: 10px;
  background: rgba(255, 152, 0, 0.1);
  border-radius: 8px;
}

/* Resend Container Compact */
.resend-container-compact {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.resend-button-compact {
  padding: 10px 24px;
  background: linear-gradient(135deg, #64b5f6 0%, #2196f3 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
}

.resend-button-compact:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
}

.resend-button-compact:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.summary-label {
  font-size: 13px;
  color: #546e7a;
  font-weight: 600;
}

.summary-value {
  font-size: 14px;
  color: #2c3e50;
  font-weight: 600;
  text-align: right;
}

.cta-message {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 14px;
  padding: 18px;
  text-align: center;
}

.cta-message p {
  color: white;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  line-height: 1.5;
}

/* Step Navigation (inside cards) */
.step-navigation {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e0e7ee;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #FF9800;
  color: white;
}

.btn-secondary {
  background: white;
  color: #2c3e50;
  border: 2px solid #e0e7ee;
}

.btn-success {
  background: #FF9800;
  color: white !important;
  justify-content: center;
}

.btn-success:hover:not(:disabled) {
  background: #FF9800;
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(255, 152, 0, 0.4);
}

.btn-icon {
  font-size: 18px;
}

/* Animations */
.slide-fade-enter-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
}

.slide-fade-enter-from {
  transform: translateX(30px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(-30px);
  opacity: 0;
}

/* Password Step Styles */
.password-strength {
  width: 100%;
  height: 6px;
  background-color: #d0d7de;
  border-radius: 3px;
  overflow: hidden;
  margin-top: 8px;
}

.strength-bar {
  height: 100%;
  transition: all 0.3s ease;
  border-radius: 3px;
}

.strength-bar.weak {
  background: linear-gradient(90deg, #f44336, #e57373);
}

.strength-bar.medium {
  background: linear-gradient(90deg, #FF9800, #ffb74d);
}

.strength-bar.strong {
  background: linear-gradient(90deg, #FF9800, #FF9800);
}

.password-hint {
  font-size: 12px;
  font-weight: 600;
  margin-top: 6px;
  display: block;
}

.password-hint::before {
  content: '🔒 Force: ';
}

.strength-bar.weak + .password-hint {
  color: #f44336;
}

.strength-bar.medium + .password-hint {
  color: #FF9800;
}

.strength-bar.strong + .password-hint {
  color: #FF9800;
}

.password-error,
.email-error {
  font-size: 12px;
  color: #f44336;
  font-weight: 600;
  margin-top: 6px;
  display: block;
}

.password-requirements {
  margin-top: 12px;
  padding: 12px 14px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%);
  border-radius: 10px;
  border-left: 3px solid #FF9800;
}

.requirements-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px 12px;
}

.requirement {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #546e7a;
  transition: all 0.3s ease;
}

.requirement.met {
  color: #FF9800;
  font-weight: 600;
}

.requirement-icon {
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  border-radius: 50%;
  background: #e0e0e0;
  color: #9e9e9e;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.requirement.met .requirement-icon {
  background: #FF9800;
  color: white;
  box-shadow: 0 2px 6px rgba(255, 152, 0, 0.3);
  transform: scale(1.05);
}

/* Responsive Design */
@media (max-width: 768px) {
  .onboarding-container {
    padding: 10px 12px;
  }

  .step-card {
    padding: 16px 14px;
  }

  .code-input {
    font-size: 24px;
    letter-spacing: 6px;
  }

  .step-navigation {
    flex-direction: column;
    gap: 8px;
  }

  .step-navigation .btn {
    width: 100%;
    padding: 12px 20px;
  }

  .requirements-grid {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .step-title {
    font-size: 20px;
  }

  .step-description {
    font-size: 13px;
    margin-bottom: 16px;
  }

  .animated-icon {
    font-size: 36px;
  }

  .role-selector {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .role-card {
    padding: 14px 12px;
  }

  .feature-list {
    gap: 6px;
  }

  .feature-item {
    padding: 8px;
    font-size: 12px;
  }
}

/* Media query pour les écrans avec hauteur réduite */
@media (max-height: 700px) {
  .step-content {
    max-height: calc(100vh - 80px);
  }

  .step-card {
    padding: 16px;
    margin: 5px 0;
  }

  .icon-container {
    margin-bottom: 8px;
  }

  .animated-icon {
    font-size: 32px;
  }

  .step-title {
    font-size: 20px;
    margin-bottom: 8px;
  }

  .step-description {
    font-size: 13px;
    margin-bottom: 16px;
  }

  .feature-list {
    margin-top: 12px;
    gap: 6px;
  }

  .feature-item {
    padding: 8px;
  }

  .onboarding-form {
    gap: 12px;
  }

  .form-group {
    gap: 4px;
  }

  .form-group input,
  .form-group select {
    padding: 8px 10px;
  }

  .step-navigation {
    margin-top: 16px;
    padding-top: 12px;
  }

  .btn {
    padding: 8px 16px;
    font-size: 13px;
  }

  .password-requirements {
    padding: 10px 12px;
    margin-top: 10px;
  }

  .role-card {
    padding: 12px 10px;
  }

  .role-icon {
    font-size: 36px;
    margin-bottom: 6px;
  }

  .role-card h3 {
    font-size: 13px;
    margin-bottom: 4px;
  }

  .role-card p {
    font-size: 11px;
  }

  .step-indicator {
    margin: 10px 0;
  }
}

/* Media query pour les très petits écrans en hauteur */
@media (max-height: 600px) {
  .step-content {
    max-height: calc(100vh - 70px);
  }

  .step-card {
    padding: 12px;
  }

  .animated-icon {
    font-size: 28px;
  }

  .step-title {
    font-size: 18px;
    margin-bottom: 6px;
  }

  .step-description {
    font-size: 12px;
    margin-bottom: 12px;
  }

  .feature-list {
    margin-top: 8px;
    gap: 4px;
  }

  .feature-item {
    padding: 6px;
    font-size: 11px;
  }

  .feature-icon {
    width: 20px;
    height: 20px;
    font-size: 10px;
  }

  .onboarding-form {
    gap: 10px;
  }

  .form-group input,
  .form-group select {
    padding: 7px 10px;
    font-size: 13px;
  }

  .step-navigation {
    margin-top: 12px;
    padding-top: 10px;
  }

  .btn {
    padding: 7px 14px;
    font-size: 12px;
  }
}
</style>